/**
 * Formats split display for messages according to specific requirements
 * singles = 1x, pairs = 2x, trio = 3x, quad = 4x etc.
 * Multiple instances are separated by slashes
 */
export function formatSplitForMessage(split: string | undefined, quantity: number): string {
  if (!split || split === 'all-together' || split === 'together') {
    // Default together format
    return `${quantity}x`;
  }

  const splitMap: { [key: string]: number } = {
    'singles': 1,
    'pairs': 2,
    'trio': 3,
    'trios': 3,
    'up-to-3': 3,
    'quad': 4,
    'quads': 4,
    'up-to-4': 4,
    'up-to-2': 2,
  };

  // Check for "X together" pattern
  const togetherMatch = split.match(/^(\d+)\s+together$/);
  if (togetherMatch) {
    const size = parseInt(togetherMatch[1]);
    return `${size}x`;
  }

  const splitSize = splitMap[split.toLowerCase()];
  if (splitSize) {
    if (splitSize === 1) {
      // For singles, show each individual ticket
      return Array(quantity).fill('1x').join('/');
    } else {
      // For "up-to-X" splits, show optimal grouping
      if (split.toLowerCase().startsWith('up-to-')) {
        const groups = [];
        let remaining = quantity;
        
        // Try to make groups of the maximum size first
        while (remaining >= splitSize) {
          groups.push(`${splitSize}x`);
          remaining -= splitSize;
        }
        
        // Add remaining tickets as singles
        while (remaining > 0) {
          groups.push('1x');
          remaining--;
        }
        
        return groups.join('/');
      } else {
        // For exact splits (pairs, trios, quads), divide evenly
        const groups = Math.ceil(quantity / splitSize);
        return Array(groups).fill(`${splitSize}x`).join('/');
      }
    }
  }

  // Fallback for unknown split types
  return `${quantity}x`;
}

/**
 * Formats split display for wanted messages - shows total needed split
 */
export function formatSplitForWantedMessage(split: string | undefined, quantity: number): string {
  if (!split || split === 'all-together' || split === 'together') {
    // Default together format
    return `${quantity}x`;
  }

  const splitMap: { [key: string]: number } = {
    'singles': 1,
    'pairs': 2,
    'trio': 3,
    'trios': 3,
    'up-to-3': 3,
    'quad': 4,
    'quads': 4,
    'up-to-4': 4,
    'up-to-2': 2,
  };

  // Check for "X together" pattern
  const togetherMatch = split.match(/^(\d+)\s+together$/);
  if (togetherMatch) {
    const size = parseInt(togetherMatch[1]);
    return `${size}x`;
  }

  const splitSize = splitMap[split.toLowerCase()];
  if (splitSize) {
    if (splitSize === 1) {
      // For singles, show each individual ticket
      return Array(quantity).fill('1x').join('/');
    } else {
      // For wanted messages, show how many groups are needed
      const groups = Math.ceil(quantity / splitSize);
      return Array(groups).fill(`${splitSize}x`).join('/');
    }
  }

  // Fallback for unknown split types
  return `${quantity}x`;
}

/**
 * Generates a wanted message for sales without emojis
 */
export function generateWantedMessage(sale: {
  eventName?: string;
  category?: string;
  block?: string;
  split?: string;
  quantity: number;
}): string {
  const eventName = sale.eventName || 'Event';
  // If block exists, show only block. Otherwise show category
  const location = sale.block || sale.category || '';
  const splitDisplay = formatSplitForWantedMessage(sale.split, sale.quantity);

  return `wanted
${eventName}
${location}
${splitDisplay}`;
}

/**
 * Generates a clean message for ticket listing without emojis
 */
export function generateTicketMessage(ticket: {
  eventName: string;
  category?: string;
  block?: string;
  split?: string;
  quantity: number;
  soldQuantity?: number;
  wantedPrice?: number;
  currency?: string;
}): string {
  const availableQty = ticket.quantity - (ticket.soldQuantity || 0);
  
  if (availableQty <= 0) {
    return '';
  }

  const eventName = ticket.eventName || 'Event';
  // If block exists, show only block. Otherwise show category
  const location = ticket.block || ticket.category || '';
  const splitDisplay = formatSplitForMessage(ticket.split, availableQty);
  const price = ticket.wantedPrice ? 
    `${getCurrencySymbol(ticket.currency || 'GBP')}${ticket.wantedPrice}` : 
    'Price on request';

  return `available
${eventName}
${location}
${splitDisplay} ${price}`;
}

// Import currency symbol function
function getCurrencySymbol(currency: string): string {
  const currencySymbols: { [key: string]: string } = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CAD': 'C$',
    'AUD': 'A$',
    'CHF': 'CHF',
    'CNY': '¥',
    'SEK': 'kr',
    'NZD': 'NZ$'
  };
  
  return currencySymbols[currency] || currency;
}